// src/canvas/ExplorerRiskAreaCard.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Card, Stack, Group, Text, Badge, Button, Tooltip, Collapse, Paper, Progress,
  Divider, TextInput, Textarea, ActionIcon, Modal, Select, Alert
} from '@mantine/core';
import {
  IconChevronDown, IconChevronRight, IconEdit, IconSparkles, IconTrash,
  IconPlus, IconDeviceFloppy, IconAlertCircle, IconX, IconCheck, IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { v4 as uuid } from 'uuid';
import RiskMitigationsInlineV2 from './components/RiskMitigationsInlineV2';
import { updateExplorerRisk, addExplorerRisk, deleteExplorerRisk } from '../redux/slices/risksSlice';
import { authFetch } from '../utils/authFetch';

// Priority colors (app standard - consistent with RiskLandscape)
const PRIORITY_COLORS = {
  Critical: '#1A1A1A',    // Black
  High: '#993333',        // Dark red
  Significant: '#B94D4D', // Lighter red
  Moderate: '#D4AA7D',    // Tan/brown
  Low: '#B8C2B7',         // Light green-gray
  Negligible: '#D8D8D8'   // Gray
};

const getPriorityColor = (priority) => {
  if (!priority) return '#EBEBEB';
  const normalized = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
  return PRIORITY_COLORS[normalized] || '#EBEBEB';
};

/**
 * ExplorerRiskAreaCard - Expandable card for Explorer organizational risk areas
 * Matches Builder's SectionAssessmentCardV2 UI/UX pattern
 */
export default function ExplorerRiskAreaCard({
  areaKey,
  areaConfig,
  risks,
  stats,
  isExpanded,
  onToggleExpand,
  canEdit,
  modelId,
  assessmentUuid,
  isMinimized = false
}) {
  const dispatch = useDispatch();

  // State for inline editing
  const [editingRiskId, setEditingRiskId] = useState(null);
  const [editedRiskName, setEditedRiskName] = useState('');

  // State for priority editing
  const [editingPriorityRiskId, setEditingPriorityRiskId] = useState(null);
  const [editedPriority, setEditedPriority] = useState('');

  // State for adding new risk
  const [showAddRisk, setShowAddRisk] = useState(false);
  const [newRiskName, setNewRiskName] = useState('');

  // State for AI refinement modal
  const [refineModalOpened, setRefineModalOpened] = useState(false);
  const [refineRiskData, setRefineRiskData] = useState(null);
  const [refineInstruction, setRefineInstruction] = useState('specific');
  const [customInstruction, setCustomInstruction] = useState('');
  const [refinedSuggestion, setRefinedSuggestion] = useState(null);
  const [isRefining, setIsRefining] = useState(false);

  // State for saving
  const [saving, setSaving] = useState(false);

  // Stats calculation
  const riskCount = stats?.riskCount || 0;
  const actionCount = stats?.actionCount || 0;
  const completedActions = stats?.completedActions || 0;
  const progressPercentage = stats?.progress || 0;
  const priorities = stats?.priorities || {};

  // Top 3 priorities for badges
  const topPriorities = Object.entries(priorities)
    .filter(([, count]) => count > 0)
    .sort((a, b) => {
      const order = { Critical: 5, High: 4, Significant: 3, Moderate: 2, Low: 1, Negligible: 0 };
      return (order[b[0]] || 0) - (order[a[0]] || 0);
    })
    .slice(0, 3);

  // Inline editing handlers
  const handleStartEdit = (risk) => {
    setEditingRiskId(risk.riskId);
    setEditedRiskName(risk.riskName || risk.name || '');
  };

  const handleCancelEdit = () => {
    setEditingRiskId(null);
    setEditedRiskName('');
  };

  const handleSaveEdit = async (risk) => {
    if (!editedRiskName.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Risk name cannot be empty',
        color: 'red'
      });
      return;
    }

    setSaving(true);
    try {
      // Update Redux state (category from risk or default)
      dispatch(updateExplorerRisk({
        assessmentUuid,
        areaKey,
        category: risk.category || 'general',
        riskId: risk.riskId,
        updates: { riskName: editedRiskName.trim(), name: editedRiskName.trim() }
      }));

      // Persist to database
      const response = await authFetch('/.netlify/functions/updateExplorerRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          riskId: risk.riskId,
          riskName: editedRiskName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save to database');
      }

      notifications.show({
        title: 'Success',
        message: 'Risk updated successfully',
        color: 'green'
      });

      handleCancelEdit();
    } catch (error) {
      console.error('[ExplorerRiskAreaCard] Failed to update risk:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update risk',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  // Priority editing handlers
  const handleStartPriorityEdit = (risk) => {
    setEditingPriorityRiskId(risk.riskId);
    setEditedPriority(risk.priority || '');
  };

  const handleCancelPriorityEdit = () => {
    setEditingPriorityRiskId(null);
    setEditedPriority('');
  };

  const handleSavePriority = async (risk) => {
    if (!editedPriority) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a priority level',
        color: 'red'
      });
      return;
    }

    setSaving(true);
    try {
      // Update Redux state
      dispatch(updateExplorerRisk({
        assessmentUuid,
        areaKey,
        category: risk.category || 'general',
        riskId: risk.riskId,
        updates: { priority: editedPriority }
      }));

      // Persist to database
      const response = await authFetch('/.netlify/functions/saveExplorerRiskPriority', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelUuid: modelId,
          assessmentUuid,
          riskId: risk.riskId,
          priority: editedPriority
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save priority to database');
      }

      notifications.show({
        title: 'Success',
        message: 'Priority updated successfully',
        color: 'green'
      });

      handleCancelPriorityEdit();
    } catch (error) {
      console.error('[ExplorerRiskAreaCard] Failed to update priority:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update priority',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  // Show rationale modal
  const handleShowRationale = (risk) => {
    modals.open({
      title: 'Priority Rationale',
      children: (
        <Stack gap="md">
          <Paper withBorder p="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>{risk.riskName || risk.name}</Text>
                <Badge
                  size="sm"
                  variant="filled"
                  style={{
                    backgroundColor: getPriorityColor(risk.priority),
                    color: risk.priority?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A'
                  }}
                >
                  {risk.priority}
                </Badge>
              </Group>
            </Stack>
          </Paper>
          <Stack gap="xs">
            <Text size="sm" fw={600} c="blue.7">AI-Generated Rationale:</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {risk.rationale || risk.evaluation?.rationale || 'No rationale available for this risk.'}
            </Text>
          </Stack>
        </Stack>
      ),
      size: 'lg'
    });
  };

  // Delete risk handler
  const handleDeleteRisk = (risk) => {
    modals.openConfirmModal({
      title: 'Delete Risk',
      children: (
        <Stack gap="sm">
          <Text size="sm">Are you sure you want to delete this risk?</Text>
          <Paper withBorder p="sm" bg="red.0">
            <Text size="sm" fw={500}>{risk.riskName || risk.name}</Text>
          </Paper>
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            This will also delete all associated mitigation actions.
          </Alert>
        </Stack>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          // Update Redux state
          dispatch(deleteExplorerRisk({
            assessmentUuid,
            areaKey,
            riskId: risk.riskId
          }));

          // Persist to database
          const response = await authFetch('/.netlify/functions/deleteExplorerRisk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ riskId: risk.riskId })
          });

          if (!response.ok) {
            throw new Error('Failed to delete from database');
          }

          notifications.show({
            title: 'Success',
            message: 'Risk deleted successfully',
            color: 'green'
          });
        } catch (error) {
          console.error('[ExplorerRiskAreaCard] Failed to delete risk:', error);
          notifications.show({
            title: 'Error',
            message: 'Failed to delete risk',
            color: 'red'
          });
        }
      }
    });
  };

  // Add risk handler
  const handleAddRisk = async () => {
    if (!newRiskName.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Risk name cannot be empty',
        color: 'red'
      });
      return;
    }

    setSaving(true);
    try {
      const newRiskId = uuid();

      // Persist to database first
      const response = await authFetch('/.netlify/functions/createExplorerRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          riskArea: areaKey,
          riskId: newRiskId,
          riskName: newRiskName.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create risk in database');
      }

      // Update Redux state (use 'general' as default category)
      dispatch(addExplorerRisk({
        assessmentUuid,
        areaKey,
        category: 'general',
        risk: {
          riskId: newRiskId,
          riskName: newRiskName.trim(),
          name: newRiskName.trim(),
          priority: 'Moderate',
          controls: []
        }
      }));

      notifications.show({
        title: 'Success',
        message: 'Risk added successfully',
        color: 'green'
      });

      setNewRiskName('');
      setShowAddRisk(false);
    } catch (error) {
      console.error('[ExplorerRiskAreaCard] Failed to add risk:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to add risk',
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  // AI refinement handlers
  const handleRefineRisk = (risk) => {
    setRefineRiskData(risk);
    setRefineModalOpened(true);
    setRefinedSuggestion(null);
    setRefineInstruction('specific');
    setCustomInstruction('');
  };

  const handleGenerateRefinement = async () => {
    setIsRefining(true);

    let userInstruction = '';
    if (refineInstruction === 'specific') {
      userInstruction = 'Make the risk more specific and focused on organizational impact';
    } else if (refineInstruction === 'technical') {
      userInstruction = 'Make the risk more technical in business/operational terms';
    } else if (refineInstruction === 'formal') {
      userInstruction = 'Make the risk more formal and suitable for executive reporting';
    } else if (refineInstruction === 'custom') {
      userInstruction = customInstruction;
    }

    try {
      const response = await authFetch('/.netlify/functions/refineRiskLLM', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          originalRiskText: refineRiskData.riskName || refineRiskData.name,
          userInstruction,
          context: { riskArea: areaConfig.label }
        })
      });

      if (!response.ok) {
        throw new Error('Refinement request failed');
      }

      const { refinedRisk } = await response.json();
      setRefinedSuggestion(refinedRisk);
    } catch (error) {
      console.error('[ExplorerRiskAreaCard] Refinement failed:', error);
      notifications.show({
        title: 'Refinement Failed',
        message: 'Could not generate refinement. Please try again.',
        color: 'red'
      });
    } finally {
      setIsRefining(false);
    }
  };

  const handleApplyRefinement = async () => {
    if (!refinedSuggestion) return;

    try {
      // Update Redux state
      dispatch(updateExplorerRisk({
        assessmentUuid,
        areaKey,
        category: refineRiskData.category || 'general',
        riskId: refineRiskData.riskId,
        updates: { riskName: refinedSuggestion, name: refinedSuggestion }
      }));

      // Persist to database
      const response = await authFetch('/.netlify/functions/updateExplorerRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          riskId: refineRiskData.riskId,
          riskName: refinedSuggestion
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save to database');
      }

      notifications.show({
        title: 'Success',
        message: 'Refined risk applied',
        color: 'green'
      });

      setRefineModalOpened(false);
      setRefineRiskData(null);
      setRefinedSuggestion(null);
    } catch (error) {
      console.error('[ExplorerRiskAreaCard] Failed to apply refinement:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to apply refinement',
        color: 'red'
      });
    }
  };

  const caret = isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />;

  // Minimized view for when another card is expanded
  if (isMinimized) {
    return (
      <Card
        withBorder
        radius="md"
        p="sm"
        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
        onClick={onToggleExpand}
        bg="gray.0"
      >
        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <IconChevronRight size={16} />
            <Text fw={600} size="sm">{areaConfig.label}</Text>
          </Group>
          <Group gap="xs">
            <Badge variant="light" color="indigo" size="xs">
              {riskCount} risks
            </Badge>
            <Badge variant="light" color="teal" size="xs">
              {progressPercentage}% done
            </Badge>
          </Group>
        </Group>
      </Card>
    );
  }

  return (
    <Card
      withBorder
      radius="md"
      p={isExpanded ? 'lg' : 'md'}
      style={{
        transition: 'all 0.3s ease',
        minHeight: isExpanded ? '400px' : 'auto'
      }}
    >
      <Stack gap="md">
        {/* Header Section */}
        <Group justify="space-between" align="flex-start">
          <Group
            gap="sm"
            align="center"
            onClick={onToggleExpand}
            style={{ cursor: 'pointer', flex: 1 }}
          >
            {caret}
            <Stack gap={2}>
              <Text fw={700} size={isExpanded ? 'lg' : 'md'}>
                {areaConfig.label}
              </Text>
              <Text size="xs" c="dimmed">
                {areaConfig.description}
              </Text>
              <Text size="xs" c="dimmed">
                {riskCount} risks identified • {actionCount} actions planned
              </Text>
            </Stack>
          </Group>

          {/* Priority Counter Badges */}
          <Group gap="xs" align="center">
            {topPriorities.map(([priority, count]) => (
              <Tooltip key={priority} label={`${priority}: ${count}`}>
                <Badge
                  variant="filled"
                  size="lg"
                  style={{
                    backgroundColor: getPriorityColor(priority),
                    color: priority?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A',
                    fontWeight: 700,
                    minWidth: '32px',
                    padding: '8px 10px'
                  }}
                >
                  {count}
                </Badge>
              </Tooltip>
            ))}
          </Group>
        </Group>

        {/* Progress Section - Always Visible */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>Mitigation Progress</Text>
            <Text size="sm" c="dimmed">{progressPercentage}%</Text>
          </Group>
          <Progress
            value={progressPercentage}
            color={progressPercentage > 75 ? 'green' : progressPercentage > 50 ? 'yellow' : 'red'}
            size="sm"
            radius="md"
          />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              {completedActions} of {actionCount} actions complete
            </Text>
          </Group>
        </Stack>

        {/* Quick Actions - Collapsed View */}
        {!isExpanded && (
          <>
            <Divider />
            <Group justify="center">
              <Tooltip label="Expand to view risk details">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconEdit size={14} />}
                  onClick={onToggleExpand}
                >
                  View Risks
                </Button>
              </Tooltip>
            </Group>
          </>
        )}

        {/* Expanded Content */}
        <Collapse in={isExpanded}>
          <Stack gap="lg">
            <Divider label="Risk Details" labelPosition="left" />

            {risks.length === 0 ? (
              <Card withBorder radius="sm" p="md" bg="gray.0">
                <Text c="dimmed" ta="center">No risks identified in this area yet.</Text>
              </Card>
            ) : (
              <Stack gap="sm">
                {risks.map((risk) => (
                  <Paper key={risk.riskId} withBorder radius="sm" p="md" bg="white">
                    <Stack gap="sm">
                      {/* Row 1: Risk Name + Action Buttons */}
                      <Group justify="space-between" align="flex-start">
                        <Stack gap={4} style={{ flex: 1 }}>
                          {editingRiskId === risk.riskId ? (
                            // EDIT MODE
                            <Stack gap="xs">
                              <TextInput
                                value={editedRiskName}
                                onChange={(e) => setEditedRiskName(e.target.value)}
                                placeholder="Risk name"
                                style={{ flex: 1 }}
                                size="sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit(risk);
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                              />
                              <Group gap="xs">
                                <Button
                                  size="xs"
                                  onClick={() => handleSaveEdit(risk)}
                                  leftSection={<IconDeviceFloppy size={12} />}
                                  loading={saving}
                                >
                                  Save
                                </Button>
                                <Button size="xs" variant="subtle" onClick={handleCancelEdit}>
                                  Cancel
                                </Button>
                              </Group>
                            </Stack>
                          ) : (
                            // VIEW MODE
                            <Stack gap="xs">
                              <Text fw={600} size="sm" style={{ wordBreak: 'break-word' }}>
                                {risk.riskName || risk.name}
                              </Text>
                              {risk.description && (
                                <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                                  {risk.description}
                                </Text>
                              )}
                            </Stack>
                          )}
                        </Stack>

                        {/* Action Buttons */}
                        {canEdit && editingRiskId !== risk.riskId && (
                          <Group gap="xs">
                            <Tooltip label="Refine with AI">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="violet"
                                onClick={() => handleRefineRisk(risk)}
                              >
                                <IconSparkles size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Edit risk name">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => handleStartEdit(risk)}
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete risk">
                              <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() => handleDeleteRisk(risk)}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        )}
                      </Group>

                      {/* Row 2: Assessment Badges */}
                      <Group gap="xs" align="center">
                        {/* Priority Badge */}
                        {editingPriorityRiskId === risk.riskId ? (
                          <Group gap="xs">
                            <Text size="xs" c="dimmed">Priority:</Text>
                            <Select
                              value={editedPriority}
                              onChange={setEditedPriority}
                              data={[
                                { value: 'Critical', label: 'Critical' },
                                { value: 'High', label: 'High' },
                                { value: 'Significant', label: 'Significant' },
                                { value: 'Moderate', label: 'Moderate' },
                                { value: 'Low', label: 'Low' },
                                { value: 'Negligible', label: 'Negligible' }
                              ]}
                              size="xs"
                              w={120}
                            />
                            <ActionIcon
                              size="xs"
                              color="green"
                              onClick={() => handleSavePriority(risk)}
                              loading={saving}
                            >
                              <IconCheck size={12} />
                            </ActionIcon>
                            <ActionIcon size="xs" color="gray" onClick={handleCancelPriorityEdit}>
                              <IconX size={12} />
                            </ActionIcon>
                          </Group>
                        ) : (
                          risk.priority && (
                            <Group gap={4}>
                              <Text size="xs" c="dimmed">Priority:</Text>
                              <Tooltip label={canEdit ? 'Click to edit priority' : risk.priority}>
                                <Badge
                                  size="sm"
                                  variant="filled"
                                  style={{
                                    backgroundColor: getPriorityColor(risk.priority),
                                    color: risk.priority?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A',
                                    cursor: canEdit ? 'pointer' : 'default'
                                  }}
                                  onClick={canEdit ? () => handleStartPriorityEdit(risk) : undefined}
                                >
                                  {risk.priority}
                                </Badge>
                              </Tooltip>
                              {(risk.rationale || risk.evaluation?.rationale) && (
                                <Tooltip label="View priority rationale">
                                  <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="blue"
                                    onClick={() => handleShowRationale(risk)}
                                  >
                                    <IconInfoCircle size={14} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          )
                        )}

                        {/* Residual Risk Badge (if evaluated) */}
                        {risk.evaluation?.residualRisk && (
                          <>
                            <Divider orientation="vertical" size="xs" color="gray.3" style={{ height: '20px' }} />
                            <Group gap={4}>
                              <Text size="xs" c="dimmed">Residual:</Text>
                              <Badge
                                variant="filled"
                                size="xs"
                                style={{
                                  backgroundColor: getPriorityColor(risk.evaluation.residualRisk),
                                  color: risk.evaluation.residualRisk?.toLowerCase() === 'critical' ? 'white' : '#1A1A1A'
                                }}
                              >
                                {risk.evaluation.residualRisk}
                              </Badge>
                            </Group>
                          </>
                        )}
                      </Group>

                      {/* Risk Mitigations */}
                      <RiskMitigationsInlineV2
                        modelId={modelId}
                        assessmentUuid={assessmentUuid}
                        risk={{
                          ...risk,
                          riskName: risk.riskName || risk.name
                        }}
                        canEdit={canEdit}
                        isExplorer={true}
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            {/* Add Risk Section */}
            {canEdit && (
              <>
                <Divider label="Add Risk" labelPosition="center" />
                {showAddRisk ? (
                  <Paper withBorder p="sm" bg="gray.0">
                    <Stack gap="xs">
                      <TextInput
                        placeholder="Describe the organizational risk..."
                        value={newRiskName}
                        onChange={(e) => setNewRiskName(e.target.value)}
                        size="sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddRisk();
                          if (e.key === 'Escape') setShowAddRisk(false);
                        }}
                      />
                      <Group gap="xs">
                        <Button
                          size="xs"
                          onClick={handleAddRisk}
                          leftSection={<IconPlus size={12} />}
                          loading={saving}
                        >
                          Add Risk
                        </Button>
                        <Button size="xs" variant="subtle" onClick={() => setShowAddRisk(false)}>
                          Cancel
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                ) : (
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setShowAddRisk(true)}
                    fullWidth
                  >
                    Add Risk to {areaConfig.label}
                  </Button>
                )}
              </>
            )}

            {/* Collapse Button */}
            <Divider />
            <Group justify="flex-end">
              <Button
                size="sm"
                variant="light"
                onClick={onToggleExpand}
              >
                Collapse View
              </Button>
            </Group>
          </Stack>
        </Collapse>

        {/* AI Refinement Modal */}
        <Modal
          opened={refineModalOpened}
          onClose={() => setRefineModalOpened(false)}
          title={
            <Group gap="xs">
              <IconSparkles size={20} color="violet" />
              <Text fw={600}>Refine Risk with AI</Text>
            </Group>
          }
          size="lg"
        >
          <Stack gap="md">
            <Paper withBorder p="sm" bg="gray.0">
              <Text size="sm" fw={500} mb="xs">Original Risk:</Text>
              <Text size="sm">{refineRiskData?.riskName || refineRiskData?.name}</Text>
            </Paper>

            <Select
              label="Refinement Type"
              value={refineInstruction}
              onChange={setRefineInstruction}
              data={[
                { value: 'specific', label: 'Make more specific and focused' },
                { value: 'technical', label: 'Make more business/operational' },
                { value: 'formal', label: 'Make more formal for executives' },
                { value: 'custom', label: 'Custom instruction' }
              ]}
            />

            {refineInstruction === 'custom' && (
              <Textarea
                label="Custom Instruction"
                placeholder="Describe how you want to refine this risk..."
                value={customInstruction}
                onChange={(e) => setCustomInstruction(e.target.value)}
                rows={3}
              />
            )}

            <Button
              onClick={handleGenerateRefinement}
              loading={isRefining}
              leftSection={<IconSparkles size={16} />}
              disabled={refineInstruction === 'custom' && !customInstruction.trim()}
            >
              Generate Refinement
            </Button>

            {refinedSuggestion && (
              <Paper withBorder p="sm" bg="blue.0">
                <Text size="sm" fw={500} mb="xs" c="blue.8">AI Suggestion:</Text>
                <Text size="sm" c="blue.9">{refinedSuggestion}</Text>
              </Paper>
            )}

            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={() => setRefineModalOpened(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleApplyRefinement}
                disabled={!refinedSuggestion}
                color="violet"
              >
                Apply Refinement
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Card>
  );
}

ExplorerRiskAreaCard.propTypes = {
  areaKey: PropTypes.string.isRequired,
  areaConfig: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired
  }).isRequired,
  risks: PropTypes.arrayOf(
    PropTypes.shape({
      riskId: PropTypes.string.isRequired,
      riskName: PropTypes.string,
      name: PropTypes.string,
      priority: PropTypes.string,
      controls: PropTypes.array
    })
  ).isRequired,
  stats: PropTypes.shape({
    riskCount: PropTypes.number,
    actionCount: PropTypes.number,
    completedActions: PropTypes.number,
    progress: PropTypes.number,
    priorities: PropTypes.object
  }),
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  modelId: PropTypes.string.isRequired,
  assessmentUuid: PropTypes.string.isRequired,
  isMinimized: PropTypes.bool
};
